import React, { useState, useCallback, useRef, useEffect } from 'react';
import { NodeProps } from '@xyflow/react';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';

interface StickyNoteData {
  text: string;
  color: string;
  onChange: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}

const colors = {
  yellow: 'bg-yellow-100 border-yellow-300',
  red: 'bg-red-100 border-red-300',
  blue: 'bg-blue-100 border-blue-300',
  green: 'bg-green-100 border-green-300',
  purple: 'bg-purple-100 border-purple-300'
};

const StickyNote: React.FC<NodeProps<StickyNoteData>> = ({ data, id }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(data.text);
  const [hasBeenEdited, setHasBeenEdited] = useState(false);
  const [size, setSize] = useState({ width: 200, height: 100 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    if (text !== data.text && hasBeenEdited) {
      data.onChange(id, text);
    }
  }, [id, text, data, hasBeenEdited]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setIsEditing(false);
      if (text !== data.text && hasBeenEdited) {
        data.onChange(id, text);
      }
    }
  }, [id, text, data, hasBeenEdited]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    data.onDelete(id);
  }, [id, data]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    setHasBeenEdited(true);
  };

  return (
    <Resizable
      width={size.width}
      height={size.height}
      onResize={(e, { size }) => {
        setSize({ width: size.width, height: size.height });
      }}
      minConstraints={[150, 100]}
      maxConstraints={[500, 500]}
      handle={
        <div className="absolute bottom-right w-4 h-4 cursor-se-resize" />
      }
    >
      <div 
        className={`rounded-lg shadow-md border-2 ${colors[data.color as keyof typeof colors]} relative`}
        style={{ width: `${size.width}px`, height: `${size.height}px` }}
        onClick={handleClick}
      >
        <div className="p-4 h-full">
          <button
            onClick={handleDelete}
            className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-red-500 hover:bg-red-100 rounded-full transition-colors"
          >
            ×
          </button>
          {isEditing ? (
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyPress}
              className={`w-full h-full p-2 rounded border-none focus:outline-none resize-none ${colors[data.color as keyof typeof colors]}`}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div 
              className="whitespace-pre-wrap h-full cursor-text" 
              onDoubleClick={handleDoubleClick}
            >
              {text}
            </div>
          )}
        </div>
      </div>
    </Resizable>
  );
};

export default StickyNote;